import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { IFHError } from 'src/apis/HttpBaseApiWrapper';
import { IRbac } from 'src/apis/__shared/rbac.interfaces';
import { useAuthService } from 'src/apis/auth/useAuthService';
import {
  LOCAL_STORAGE_CHANGE_EVENT,
  localStorageGet,
} from 'src/utils/localStorage';

type TQueryError = IFHError<{ message: string }>;
export const getRbacKey = 'get-rbac';

function subscribeToTokenStore(callback: () => void) {
  const handleStorageChange = () => callback();
  window.addEventListener('storage', handleStorageChange);
  window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleStorageChange);
  };
}

function getTokenSnapshot() {
  return localStorageGet('token');
}

export const useRbacHook = (
  options?: UseQueryOptions<IRbac, TQueryError>
): UseQueryResult<IRbac, TQueryError> => {
  const { getRbac } = useAuthService();
  const getRbacRef = useRef(getRbac);
  getRbacRef.current = getRbac;

  const queryFn = useCallback(() => getRbacRef.current(), []);

  const token = useSyncExternalStore(
    subscribeToTokenStore,
    getTokenSnapshot,
    getTokenSnapshot
  );
  const hasToken = Boolean(token);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[RBAC] query toggle', { hasToken });
    }
  }, [hasToken]);

  return useQuery<IRbac, TQueryError>({
    queryKey: [getRbacKey],
    queryFn,
    enabled: hasToken,
    ...options,
  });
};
